/**
 * "Work through these" (brief journey 1): step through any filtered list one
 * record at a time. Frontend-only (EA §5.5): the record URL carries the list
 * query it came from and the position in it, and each step re-reads the list,
 * so a record a command took out of the queue is not skipped over.
 */
export interface WorkThroughParams {
  /** The list's query string, as `formatListQuery` writes it. */
  through: string;
  /** 1-based position across pages. */
  at: number;
}

export function workThroughSearch(params: WorkThroughParams): string {
  return new URLSearchParams({
    through: params.through,
    at: String(params.at),
  }).toString();
}

export function parseWorkThrough(search: string): WorkThroughParams | null {
  const params = new URLSearchParams(search);
  const through = params.get("through");
  const at = Number(params.get("at"));
  if (through === null || !Number.isInteger(at) || at < 1) return null;
  return { through, at };
}

/** What the list holds around the position, read fresh. */
export interface WorkThroughRead {
  total: number;
  keyBefore: string | null;
  keyAt: string | null;
  keyAfter: string | null;
}

export type WorkThroughState =
  | { kind: "empty" }
  | {
      kind: "walking";
      /** False when the record has left the list (a command moved it on). */
      isInList: boolean;
      position: number;
      total: number;
      previous: { key: string; at: number } | null;
      next: { key: string; at: number } | null;
    };

/**
 * Where Previous and Next go. While the record is still at its position they
 * are its neighbours; once it has left, what now sits at its position is Next,
 * because everything after it moved up one.
 */
export function workThroughState(
  at: number,
  recordKey: string,
  read: WorkThroughRead,
): WorkThroughState {
  if (read.total === 0) return { kind: "empty" };
  const isInList = read.keyAt === recordKey;
  return {
    kind: "walking",
    isInList,
    position: Math.min(at, read.total),
    total: read.total,
    previous: read.keyBefore ? { key: read.keyBefore, at: at - 1 } : null,
    next: isInList
      ? read.keyAfter
        ? { key: read.keyAfter, at: at + 1 }
        : null
      : read.keyAt
        ? { key: read.keyAt, at }
        : null,
  };
}
